import React, { useState, useEffect } from 'react';
import { getVerificationPhotoUrl, rescoreVerification, overrideSpaceVerification, getReferencePhotos, getReferencePhotoUrl } from '../services/api';

const scoreBarColor = (value) => {
  if (value >= 80) return '#22c55e';
  if (value >= 60) return '#eab308';
  return '#ef4444';
};

const statusBadge = (status) => {
  const styles = {
    APPROVED: { backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
    ADMIN_APPROVED: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
    REVIEW_REQUIRED: { backgroundColor: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' },
    ESCALATED: { backgroundColor: '#fed7aa', color: '#9a3412', border: '1px solid #fdba74' },
    ADMIN_REJECTED: { backgroundColor: '#fecaca', color: '#991b1b', border: '1px solid #fca5a5' },
    FLAGGED: { backgroundColor: '#fecaca', color: '#991b1b', border: '1px solid #fca5a5' },
    IN_PROGRESS: { backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' },
    SCORING: { backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' },
    PENDING: { backgroundColor: '#f3f4f6', color: 'var(--text-muted)', border: '1px solid var(--border-medium)' },
  };
  return styles[status] || styles.PENDING;
};

const statusIcon = (status) => {
  const icons = {
    APPROVED: '\u2713',
    ADMIN_APPROVED: '\u2713',
    REVIEW_REQUIRED: '\u26A0',
    ESCALATED: '\u23F3',
    ADMIN_REJECTED: '\u2717',
    SCORING: '\u23F3',
    PENDING: '\u2022',
  };
  return icons[status] || '\u2022';
};

// Pick display name based on browser language (Spanish → name, else → name_en)
const isSpanish = () => (navigator.language || '').toLowerCase().startsWith('es');
const displaySpaceName = (spaceName, spacesConfig) => {
  const config = spacesConfig.find(s => s.name === spaceName);
  if (!config) return spaceName;
  return isSpanish() ? (config.name || config.name_en) : (config.name_en || config.name);
};

// ───────────────────────────────────────────────
// GUIDED MODE: Per-space breakdown
// ───────────────────────────────────────────────

const GuidedVerificationView = ({ verification, photos, onUpdate }) => {
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [expandedSpace, setExpandedSpace] = useState(null);
  const [overriding, setOverriding] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [feedbackMsg, setFeedbackMsg] = useState(null);
  const [refPhotos, setRefPhotos] = useState([]);

  useEffect(() => {
    if (verification.property_id) {
      getReferencePhotos(verification.property_id)
        .then(setRefPhotos)
        .catch(() => {});
    }
  }, [verification.property_id]);

  const spaceVerifications = verification.space_verifications || [];
  const flowState = verification.flow_state || {};
  const spaces = flowState.spaces || [];
  const spacesConfig = flowState.spaces_config || [];

  // Group space verifications by space_name, get latest attempt per space
  const latestBySpace = {};
  const historyBySpace = {};
  spaceVerifications.forEach(sv => {
    if (!historyBySpace[sv.space_name]) historyBySpace[sv.space_name] = [];
    historyBySpace[sv.space_name].push(sv);
    if (!latestBySpace[sv.space_name] || sv.attempt_number > latestBySpace[sv.space_name].attempt_number) {
      latestBySpace[sv.space_name] = sv;
    }
  });

  // Sort history by attempt number
  Object.values(historyBySpace).forEach(arr => arr.sort((a, b) => a.attempt_number - b.attempt_number));

  // Get photos for a specific space + attempt
  const getPhotosForSpace = (spaceName, attempt) => {
    return photos.filter(p => p.space_name === spaceName && p.attempt_number === attempt);
  };

  const handleOverride = async (svId, decision) => {
    setOverriding(svId);
    setFeedbackMsg(null);
    try {
      const result = await overrideSpaceVerification(svId, decision, adminComment.trim() || null);
      setAdminComment('');
      const action = decision === 'ADMIN_APPROVED' ? 'approved' : 'rejected';
      if (result.whatsapp_sent) {
        setFeedbackMsg({ type: 'success', text: `Space ${action} — WhatsApp sent to cleaner` });
      } else {
        setFeedbackMsg({ type: 'warn', text: `Space ${action} — WhatsApp not sent (no phone on file)` });
      }
      setTimeout(() => setFeedbackMsg(null), 4000);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Override failed:', err);
      setFeedbackMsg({ type: 'error', text: 'Override failed — check console' });
      setTimeout(() => setFeedbackMsg(null), 4000);
    } finally {
      setOverriding(null);
    }
  };

  // Calculate stats
  const totalRequired = spacesConfig.filter(s => s.required !== false).length || spaces.length;
  const approvedCount = Object.values(latestBySpace).filter(sv =>
    ['APPROVED', 'ADMIN_APPROVED'].includes(sv.status)
  ).length;
  const avgScore = Object.values(latestBySpace).length > 0
    ? Math.round(Object.values(latestBySpace).reduce((sum, sv) => sum + (sv.overall_score || 0), 0) / Object.values(latestBySpace).length)
    : 0;

  return (
    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border-medium)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
            AYNI Verified Clean
          </span>
          <span style={{
            fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px',
            ...statusBadge(verification.status)
          }}>
            {verification.status}
          </span>
        </div>
        <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
          {verification.status === 'APPROVED' || verification.status === 'ADMIN_APPROVED' || verification.status === 'FLAGGED'
            ? `${approvedCount}/${totalRequired} spaces | Avg: ${avgScore}/100`
            : `${approvedCount}/${totalRequired} spaces completed`
          }
        </span>
      </div>

      {/* Feedback banner */}
      {feedbackMsg && (
        <div style={{
          padding: '6px 12px', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', fontWeight: '500',
          backgroundColor: feedbackMsg.type === 'success' ? '#dcfce7' : feedbackMsg.type === 'warn' ? '#fef9c3' : '#fecaca',
          color: feedbackMsg.type === 'success' ? '#166534' : feedbackMsg.type === 'warn' ? '#854d0e' : '#991b1b',
          border: `1px solid ${feedbackMsg.type === 'success' ? '#bbf7d0' : feedbackMsg.type === 'warn' ? '#fde68a' : '#fca5a5'}`,
        }}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Per-space rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {spaces.map((spaceName, idx) => {
          const sv = latestBySpace[spaceName];
          const config = spacesConfig.find(s => s.name === spaceName) || {};
          const isRequired = config.required !== false;
          const isExpanded = expandedSpace === spaceName;
          const history = historyBySpace[spaceName] || [];
          const spacePhotos = sv ? getPhotosForSpace(spaceName, sv.attempt_number) : [];

          return (
            <div key={spaceName}>
              {/* Space row */}
              <div
                onClick={() => setExpandedSpace(isExpanded ? null : spaceName)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px',
                  backgroundColor: isExpanded ? 'var(--bg-elevated)' : 'var(--bg-card)',
                  borderRadius: '6px', border: '1px solid var(--border-medium)', cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
              >
                {/* Status icon */}
                <span style={{
                  fontSize: '14px', width: '20px', textAlign: 'center',
                  color: sv ? (sv.status === 'APPROVED' || sv.status === 'ADMIN_APPROVED' ? '#22c55e' :
                    sv.status === 'ADMIN_REJECTED' ? '#ef4444' :
                    sv.status === 'REVIEW_REQUIRED' ? '#eab308' :
                    sv.status === 'ESCALATED' ? '#f97316' : '#9ca3af') : '#d1d5db'
                }}>
                  {sv ? statusIcon(sv.status) : '\u2022'}
                </span>

                {/* Space name */}
                <span style={{ flex: 1, fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {displaySpaceName(spaceName, spacesConfig)}
                  {!isRequired && <span style={{ fontSize: '10px', color: 'var(--text-faint)', marginLeft: '4px' }}>(optional)</span>}
                </span>

                {/* Score */}
                {sv && sv.overall_score != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '120px' }}>
                    <div style={{ flex: 1, height: '4px', backgroundColor: 'var(--border-medium)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${sv.overall_score}%`, height: '100%', backgroundColor: scoreBarColor(sv.overall_score), borderRadius: '2px' }} />
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-primary)', width: '30px', textAlign: 'right' }}>
                      {sv.overall_score}
                    </span>
                  </div>
                )}

                {/* Attempt indicator */}
                {sv && sv.attempt_number > 1 && (
                  <span style={{ fontSize: '10px', color: 'var(--text-faint)' }}>
                    Attempt {sv.attempt_number}
                  </span>
                )}

                {/* Status badge */}
                {sv && (
                  <span style={{
                    fontSize: '10px', fontWeight: '500', padding: '1px 6px', borderRadius: '8px',
                    ...statusBadge(sv.status)
                  }}>
                    {sv.status.replace('_', ' ')}
                  </span>
                )}

                {/* Expand arrow */}
                <span style={{ fontSize: '10px', color: 'var(--text-faint)', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                  &#9654;
                </span>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ marginLeft: '28px', padding: '8px 0', borderLeft: '2px solid var(--border-medium)', paddingLeft: '12px' }}>
                  {/* Reference photos (gold standard) */}
                  {(() => {
                    const refs = refPhotos.filter(p => p.space_name === spaceName);
                    if (refs.length === 0) return null;
                    return (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--color-primary)', textTransform: 'uppercase' }}>Reference</span>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                          {refs.map((ref, ridx) => (
                            <img
                              key={ref.id}
                              src={getReferencePhotoUrl(ref.file_path)}
                              alt="Reference"
                              onClick={(e) => { e.stopPropagation(); setLightboxPhotos(refs.map(r => ({ ...r, _isRef: true }))); setLightboxIdx(ridx); }}
                              style={{ width: '70px', height: '52px', objectFit: 'cover', borderRadius: '4px', border: '2px solid var(--color-primary)', opacity: 0.9, cursor: 'pointer' }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Latest attempt photos */}
                  {spacePhotos.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Submitted</span>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                        {spacePhotos.map((photo, pidx) => (
                          <img
                            key={photo.id}
                            src={getVerificationPhotoUrl(photo.file_path)}
                            alt={`${displaySpaceName(spaceName, spacesConfig)} photo ${pidx + 1}`}
                            onClick={(e) => { e.stopPropagation(); setLightboxPhotos(spacePhotos); setLightboxIdx(pidx); }}
                            style={{
                              width: '70px', height: '52px', objectFit: 'cover', borderRadius: '4px',
                              border: '1px solid var(--border-medium)', cursor: 'pointer'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Notes */}
                  {sv && sv.ai_notes && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', fontStyle: 'italic', lineHeight: '1.4' }}>
                      {sv.ai_notes}
                    </div>
                  )}

                  {/* Previous attempts */}
                  {history.length > 1 && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-faint)', textTransform: 'uppercase' }}>
                        Previous attempts
                      </span>
                      {history.slice(0, -1).map(prev => {
                        const prevPhotos = getPhotosForSpace(spaceName, prev.attempt_number);
                        return (
                          <div key={prev.id} style={{ marginTop: '4px', padding: '6px 8px', backgroundColor: 'var(--bg-elevated)', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '600' }}>
                                Attempt {prev.attempt_number}: {prev.overall_score}/100
                              </span>
                            </div>
                            {prev.ai_notes && (
                              <div style={{ fontSize: '10px', color: '#b0b8c4', fontStyle: 'italic', marginTop: '2px', lineHeight: '1.4' }}>
                                {prev.ai_notes}
                              </div>
                            )}
                            {prevPhotos.length > 0 && (
                              <span
                                style={{ fontSize: '10px', color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={(e) => { e.stopPropagation(); setLightboxPhotos(prevPhotos); setLightboxIdx(0); }}
                              >
                                view photos
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Admin override: comment box + approve/reject buttons */}
                  {sv && (sv.status === 'ESCALATED' || sv.status === 'REVIEW_REQUIRED') && (
                    <div style={{ marginTop: '6px' }} onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={adminComment}
                        onChange={(e) => setAdminComment(e.target.value)}
                        placeholder={isSpanish() ? 'Comentario para la limpiadora (opcional)' : 'Comment for the cleaner (optional)'}
                        style={{
                          width: '100%', fontSize: '11px', padding: '6px 8px', borderRadius: '4px',
                          border: '1px solid var(--border-input)', resize: 'vertical', minHeight: '40px',
                          fontFamily: 'inherit'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        <button
                          onClick={() => handleOverride(sv.id, 'ADMIN_APPROVED')}
                          disabled={overriding === sv.id}
                          style={{
                            fontSize: '10px', padding: '2px 10px', borderRadius: '4px',
                            border: '1px solid #bbf7d0', backgroundColor: '#dcfce7', color: '#166534',
                            cursor: 'pointer', opacity: overriding === sv.id ? 0.5 : 1
                          }}
                        >
                          {overriding === sv.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleOverride(sv.id, 'ADMIN_REJECTED')}
                          disabled={overriding === sv.id}
                          style={{
                            fontSize: '10px', padding: '2px 10px', borderRadius: '4px',
                            border: '1px solid #fca5a5', backgroundColor: '#fecaca', color: '#991b1b',
                            cursor: 'pointer', opacity: overriding === sv.id ? 0.5 : 1
                          }}
                        >
                          {overriding === sv.id ? '...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightboxPhotos && (
        <div
          onClick={() => setLightboxPhotos(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, cursor: 'pointer'
          }}
        >
          <img
            src={lightboxPhotos[lightboxIdx]?._isRef
              ? getReferencePhotoUrl(lightboxPhotos[lightboxIdx]?.file_path)
              : getVerificationPhotoUrl(lightboxPhotos[lightboxIdx]?.file_path)}
            alt="Full size"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }}
            onClick={(e) => e.stopPropagation()}
          />
          {lightboxPhotos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + lightboxPhotos.length) % lightboxPhotos.length); }}
                style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '32px', color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}
              >&#8249;</button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % lightboxPhotos.length); }}
                style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '32px', color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}
              >&#8250;</button>
            </>
          )}
          <span style={{ position: 'absolute', bottom: '20px', color: '#fff', fontSize: '14px' }}>
            {lightboxIdx + 1} / {lightboxPhotos.length}
          </span>
        </div>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────
// BULK MODE: Legacy flat view (backward compat)
// ───────────────────────────────────────────────

const BulkVerificationView = ({ verification, onRescore }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [rescoring, setRescoring] = useState(false);

  const photos = verification.photos || [];
  const score = verification.score;

  const handleRescore = async (e) => {
    e.stopPropagation();
    setRescoring(true);
    try {
      const updated = await rescoreVerification(verification.id);
      if (onRescore) onRescore(updated);
    } catch (err) {
      console.error('Rescore failed:', err);
    } finally {
      setRescoring(false);
    }
  };

  const scoreItems = score ? [
    { label: 'Beds Made', value: score.beds_made },
    { label: 'Floor Clean', value: score.floor_clean },
    { label: 'No Trash', value: score.trash_visible },
    { label: 'Bathroom', value: score.bathroom_clean },
    { label: 'Kitchen', value: score.kitchen_clear },
    { label: 'No Damage', value: score.damage_detected },
  ] : [];

  return (
    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border-medium)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Verified Clean
          </span>
          <span style={{
            fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px',
            ...statusBadge(verification.status)
          }}>
            {verification.status}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
            {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </span>
          {photos.length > 0 && (
            <button
              onClick={handleRescore}
              disabled={rescoring}
              style={{
                fontSize: '11px', padding: '3px 10px', borderRadius: '6px',
                border: '1px solid var(--border-input)', backgroundColor: 'var(--bg-input)', cursor: 'pointer',
                color: 'var(--text-primary)', opacity: rescoring ? 0.5 : 1
              }}
            >
              {rescoring ? 'Scoring...' : 'Re-score'}
            </button>
          )}
        </div>
      </div>

      {/* Photos */}
      {photos.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: score ? '12px' : '0' }}>
          {photos.map((photo, idx) => (
            <img
              key={photo.id}
              src={getVerificationPhotoUrl(photo.file_path)}
              alt={`Cleaning photo ${idx + 1}`}
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(idx); }}
              style={{
                width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px',
                border: '1px solid var(--border-medium)', cursor: 'pointer',
              }}
            />
          ))}
        </div>
      )}

      {/* Score Bars */}
      {score && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
          {scoreItems.map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '70px', flexShrink: 0 }}>{label}</span>
              {value != null ? (
                <>
                  <div style={{ flex: 1, height: '6px', backgroundColor: 'var(--border-medium)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${value}%`, height: '100%', backgroundColor: scoreBarColor(value), borderRadius: '3px' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-primary)', width: '28px', textAlign: 'right' }}>{value}</span>
                </>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--text-faint)', fontStyle: 'italic' }}>N/A</span>
              )}
            </div>
          ))}
          {/* Overall */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', paddingTop: '6px', borderTop: '1px solid var(--border-medium)' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>Overall: {score.overall_score}/100</span>
            <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>Confidence: {(score.confidence * 100).toFixed(0)}%</span>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          onClick={() => setLightboxIdx(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, cursor: 'pointer'
          }}
        >
          <img
            src={getVerificationPhotoUrl(photos[lightboxIdx]?.file_path)}
            alt="Full size"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }}
            onClick={(e) => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + photos.length) % photos.length); }}
                style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '32px', color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}
              >&#8249;</button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % photos.length); }}
                style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '32px', color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}
              >&#8250;</button>
            </>
          )}
          <span style={{ position: 'absolute', bottom: '20px', color: '#fff', fontSize: '14px' }}>
            {lightboxIdx + 1} / {photos.length}
          </span>
        </div>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────
// MAIN COMPONENT: Routes to guided or bulk view
// ───────────────────────────────────────────────

const VerificationPanel = ({ verification, onRescore }) => {
  if (!verification) return null;

  const hasSpaceView = verification.verification_mode === 'guided' || verification.verification_mode === 'bulk_classify';

  if (hasSpaceView) {
    return (
      <GuidedVerificationView
        verification={verification}
        photos={verification.photos || []}
        onUpdate={onRescore ? () => onRescore() : undefined}
      />
    );
  }

  return <BulkVerificationView verification={verification} onRescore={onRescore} />;
};

export default VerificationPanel;
